import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';

// Suppress noisy third-party console messages so dev console stays useful.
// IMPORTANT: This must run before any other module imports so the wrapper is
// in place when third-party libraries (Google Identity Services, etc.) capture
// the console reference.
const NOISY_PATTERNS = [
  'React DevTools',
  'react-devtools',
  // Google Identity Services logs origin/config issues to console.error even
  // though sign-in still works. Filter them out so dev console is clean.
  'GSI_LOGGER',
  'credential_button_library',
  'The given origin is not allowed for the given client ID',
  'gis_client_id',
];

function makeSuppressor(method) {
  const original = console[method];
  console[method] = (...args) => {
    const text = args.map((a) => {
      if (typeof a === 'string') return a;
      try { return JSON.stringify(a); } catch (_) { return ''; }
    }).join(' ');
    if (NOISY_PATTERNS.some((p) => text.includes(p))) return;
    original.apply(console, args);
  };
}

makeSuppressor('log');
makeSuppressor('info');
makeSuppressor('warn');
makeSuppressor('error');

// Load runtime config so API URL can be set without rebuilding (e.g. for AWS – photos and API calls use this)
async function loadRuntimeConfig() {
  const timeoutMs = 3000;
  try {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    const res = await fetch('/config.json', { signal: controller.signal });
    clearTimeout(id);
    if (res.ok) {
      const config = await res.json();
      if (config.apiBaseUrl != null && String(config.apiBaseUrl).trim() !== '') {
        window.__API_BASE_URL__ = String(config.apiBaseUrl).trim().replace(/\/+$/, '');
      }
    }
  } catch (_) {
    // No config.json or network error – use build-time REACT_APP_API_BASE_URL or '/api'
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));

// Load config first, then App (so services get runtime API URL).
// NOTE: StrictMode is intentionally NOT enabled here. It double-invokes
// effects in development mode, which causes third-party libraries that
// register globals (Google Identity Services, MSAL) to fire their init
// twice and emit harmless-but-noisy warnings. We rely on lint, types, and
// tests to catch the issues StrictMode would surface.
loadRuntimeConfig().then(async () => {
  const { default: App } = await import('./App');
  root.render(<App />);
});